<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute } from 'vue-router';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { FilterMatchMode } from '@primevue/core/api';
import type { DataTableFilterEvent } from 'primevue/datatable';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpTextField from '@/components/KpTextField.vue';
import { StromerzeugungsanlagenCollection } from '@/models/Stromerzeugungsanlagen';

const route = useRoute();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'id' },
    { direction: 'desc', property: 'jahr' },
    { direction: 'asc', property: 'kanton_id' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const { collection, serverTotal } = useCollection(new StromerzeugungsanlagenCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

type Filter = {
    property: string;
    value: any;
    id: string;
    operator?: string;
};
const filters = ref({
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    kanton_id: { value: null, matchMode: FilterMatchMode.EQUALS },
    jahr: { value: null, matchMode: FilterMatchMode.EQUALS },
    quelle: { value: null, matchMode: FilterMatchMode.CONTAINS },
    referenz: { value: null, matchMode: FilterMatchMode.CONTAINS },
    strassenname: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hausnummer: { value: null, matchMode: FilterMatchMode.CONTAINS },
    plz: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ort: { value: null, matchMode: FilterMatchMode.CONTAINS },
    egid: { value: null, matchMode: FilterMatchMode.CONTAINS },
    bfs_gemeindenummer: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsart_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    bauperiode: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    realisierte_leistung_erstinbetriebnahme: { value: null, matchMode: FilterMatchMode.CONTAINS },
    realisierte_leistung_inkl_erweiterungen: { value: null, matchMode: FilterMatchMode.CONTAINS },
    speicherkraftwerk: { value: null, matchMode: FilterMatchMode.CONTAINS },
    geplantes_datum_inbetriebnahme: { value: null, matchMode: FilterMatchMode.CONTAINS },
    datum_inbetriebnahme: { value: null, matchMode: FilterMatchMode.CONTAINS },
    jahresproduktion: { value: null, matchMode: FilterMatchMode.CONTAINS },
    technologie_code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    support_schema: { value: null, matchMode: FilterMatchMode.CONTAINS },
    periodizitaet: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anlagenstatus_kev: { value: null, matchMode: FilterMatchMode.CONTAINS },
    betriebsstatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hkn: { value: null, matchMode: FilterMatchMode.CONTAINS },
    technologie_stromerzeugung_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    technologie_stromerzeugung: { value: null, matchMode: FilterMatchMode.CONTAINS },
    leistung: { value: null, matchMode: FilterMatchMode.CONTAINS },
    produktion: { value: null, matchMode: FilterMatchMode.CONTAINS },
    produktiv: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ganzes_jahr_in_betrieb: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anzahl_monate_in_betrieb: { value: null, matchMode: FilterMatchMode.CONTAINS },
    nutzungsart: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const filter: Ref<Filter[]> = ref([]);
function onFilterChange(filterEvent: DataTableFilterEvent) {
    const prop = 'value';
    filter.value = [];
    for (const key in filterEvent.filters) {
        // @ts-expect-error it's save, do it!
        const value = filterEvent.filters[key].hasOwnProperty(prop) ? filterEvent.filters[key][prop] : null;
        filter.value.push({
            property: key,
            id: key,
            operator: '=',
            value,
        });
    }
    collection.setQueryParams({ filter: filter.value });
    collection.query();
}

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :showRecordEditBtn="false"
            :showRecordDeleteBtn="false"
            :showReloadBtn="false"
            :showCsvDownloadBtn="false"
            :showAddBtn="false"
            :showCopyBtn="false"
            :collection="collection"
            v-model:table-sort="tableSort"
            v-model:filters="filters"
            :title="$t('stromerzeugungsanlagen')"
            filterDisplay="row"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @filter="onFilterChange"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="kanton"
                sortable
                :header="$t('kanton')"
                style="width: 5rem"
                :showFilterMenu="false"
                sortField="kanton_id"
                filterField="kanton_id"
            >
                <template #filter="{ filterModel, filterCallback }">
                    <KpMandantKantonSelect
                        v-model="filterModel.value"
                        fluid
                        @change="filterCallback()"
                    ></KpMandantKantonSelect>
                </template>
            </Column>
            <Column field="jahr" sortable :header="$t('jahr')" style="width: 5rem" :showFilterMenu="false">
                <template #filter="{ filterModel, filterCallback }">
                    <KpJahrSelect
                        fluid
                        :start-year="2015"
                        v-model="filterModel.value"
                        @change="filterCallback()"
                    ></KpJahrSelect> </template
            ></Column>
            <Column field="quelle" sortable :header="$t('quelle')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="referenz" sortable :header="$t('referenz')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="strassenname" sortable :header="$t('strassenname')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="hausnummer" sortable :header="$t('hausnummer')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="plz" sortable :header="$t('plz')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="ort" sortable :header="$t('ort')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="egid" sortable :header="$t('egid')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="bfs_gemeindenummer" sortable :header="$t('bfs_gemeindenummer')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="nutzungsart" sortable :header="$t('nutzungsart')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="bauperiode" sortable :header="$t('bauperiode')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="name" sortable :header="$t('name')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="realisierte_leistung_erstinbetriebnahme"
                sortable
                :header="$t('realisierte_leistung_erstinbetriebnahme')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column
                field="realisierte_leistung_inkl_erweiterungen"
                sortable
                :header="$t('realisierte_leistung_inkl_erweiterungen')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="speicherkraftwerk" sortable :header="$t('speicherkraftwerk')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="geplantes_datum_inbetriebnahme" sortable :header="$t('geplantes_datum_inbetriebnahme')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="datum_inbetriebnahme" sortable :header="$t('datum_inbetriebnahme')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="jahresproduktion" sortable :header="$t('jahresproduktion')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="technologie_code" sortable :header="$t('technologie_code')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="support_schema" sortable :header="$t('support_schema')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="periodizitaet" sortable :header="$t('periodizitaet')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anlagenstatus_kev" sortable :header="$t('anlagenstatus_kev')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="betriebsstatus" sortable :header="$t('betriebsstatus')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="hkn" sortable :header="$t('hkn')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="technologie_stromerzeugung_id" sortable :header="$t('technologie_stromerzeugung_id')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="technologie_stromerzeugung" sortable :header="$t('technologie_stromerzeugung')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="leistung" sortable :header="$t('leistung')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="produktion" sortable :header="$t('produktion')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="produktiv" sortable :header="$t('produktiv')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="ganzes_jahr_in_betrieb" sortable :header="$t('ganzes_jahr_in_betrieb')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
            <Column field="anzahl_monate_in_betrieb" sortable :header="$t('anzahl_monate_in_betrieb')"
                ><template #filter="{ filterModel, filterCallback }">
                    <KpTextField v-model="filterModel.value" @input="filterCallback()" />
                </template>
            </Column>
        </KpBaseCollectionGrid>
    </div>
</template>
